// Monotone-cubic path building (no DOM, unit-tested).
//
// The interpolation is Fritsch–Carlson monotone cubic (the algorithm behind
// d3's curveMonotoneX): smooth through the samples but NEVER overshooting
// them — on telemetry that honesty matters, a smoothed depth trace must not
// dip below its real minimum. Implemented here so the chart library carries
// zero chart deps (ADR 0131).

export interface XY {
  x: number;
  y: number;
}

const fmt = (n: number): string => (Math.round(n * 100) / 100).toString();

function sign(n: number): number {
  return n < 0 ? -1 : 1;
}

/** Fritsch–Carlson tangent at the middle of three points. */
function slope3(x0: number, y0: number, x1: number, y1: number, x2: number, y2: number): number {
  const h0 = x1 - x0;
  const h1 = x2 - x1;
  const s0 = (y1 - y0) / (h0 !== 0 ? h0 : h1 < 0 ? -0 : 0);
  const s1 = (y2 - y1) / (h1 !== 0 ? h1 : h0 < 0 ? -0 : 0);
  const p = (s0 * h1 + s1 * h0) / (h0 + h1);
  const m = (sign(s0) + sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p));
  return Number.isNaN(m) ? 0 : m;
}

/** One-sided tangent at an endpoint, given the far tangent. */
function slope2(x0: number, y0: number, x1: number, y1: number, t: number): number {
  const h = x1 - x0;
  return h !== 0 ? (3 * (y1 - y0)) / h - t : t;
}

/**
 * The segments of a monotone cubic through `pts`, continuing a path already
 * positioned at pts[0]. Two points degrade to a straight segment (no
 * curvature information), exactly like d3's curveMonotoneX.
 */
function monotoneSegments(pts: XY[]): string {
  const n = pts.length;
  if (n < 2) return '';
  if (n === 2) {
    const p1 = pts[1]!;
    return `L${fmt(p1.x)},${fmt(p1.y)}`;
  }
  let d = '';
  let t0 = 0;
  for (let i = 0; i < n - 1; i += 1) {
    const p0 = pts[i]!;
    const p1 = pts[i + 1]!;
    let t1: number;
    if (i + 2 < n) {
      const p2 = pts[i + 2]!;
      t1 = slope3(p0.x, p0.y, p1.x, p1.y, p2.x, p2.y);
    } else {
      t1 = slope2(p0.x, p0.y, p1.x, p1.y, t0);
    }
    if (i === 0) t0 = slope2(p0.x, p0.y, p1.x, p1.y, t1);
    const dx = (p1.x - p0.x) / 3;
    d +=
      `C${fmt(p0.x + dx)},${fmt(p0.y + dx * t0)}` +
      ` ${fmt(p1.x - dx)},${fmt(p1.y - dx * t1)}` +
      ` ${fmt(p1.x)},${fmt(p1.y)}`;
    t0 = t1;
  }
  return d;
}

export type CurveKind = 'smooth' | 'linear' | 'step';

/** Straight segments continuing a path positioned at pts[0]. */
function linearSegments(pts: XY[]): string {
  return pts
    .slice(1)
    .map((p) => `L${fmt(p.x)},${fmt(p.y)}`)
    .join('');
}

/** Step-after segments: hold each value until the next sample lands. */
function stepSegments(pts: XY[]): string {
  let d = '';
  for (let i = 1; i < pts.length; i += 1) {
    const prev = pts[i - 1]!;
    const p = pts[i]!;
    d += `L${fmt(p.x)},${fmt(prev.y)}L${fmt(p.x)},${fmt(p.y)}`;
  }
  return d;
}

function segmentsFor(pts: XY[], curve: CurveKind): string {
  if (curve === 'step') return stepSegments(pts);
  if (curve === 'linear') return linearSegments(pts);
  return monotoneSegments(pts);
}

/** Line path in the requested curve style. Empty string for <2 points. */
export function linePathFor(pts: XY[], curve: CurveKind): string {
  if (pts.length < 2) return '';
  const first = pts[0]!;
  return `M${fmt(first.x)},${fmt(first.y)}${segmentsFor(pts, curve)}`;
}

/** Closed area under a line of the requested curve style. */
export function areaPathFor(pts: XY[], baselineY: number, curve: CurveKind): string {
  if (pts.length < 2) return '';
  const last = pts[pts.length - 1]!;
  const first = pts[0]!;
  return `${linePathFor(pts, curve)}L${fmt(last.x)},${fmt(baselineY)}L${fmt(first.x)},${fmt(baselineY)}Z`;
}

/** Closed envelope between two lines of the requested curve style. */
export function bandPathFor(top: XY[], bottom: XY[], curve: CurveKind): string {
  if (top.length < 2 || bottom.length < 2) return '';
  const back = [...bottom].reverse();
  const backFirst = back[0]!;
  return `${linePathFor(top, curve)}L${fmt(backFirst.x)},${fmt(backFirst.y)}${segmentsFor(back, curve)}Z`;
}

/** SVG path for a smooth line through the points. Empty string for <2 points. */
export function monotonePath(pts: XY[]): string {
  if (pts.length < 2) return '';
  const first = pts[0]!;
  return `M${fmt(first.x)},${fmt(first.y)}${monotoneSegments(pts)}`;
}

/** Straight polyline path (used where smoothing would misread, e.g. steps). */
export function linearPath(pts: XY[]): string {
  if (pts.length < 2) return '';
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${fmt(p.x)},${fmt(p.y)}`).join('');
}

/** Closed area between a smooth top line and a horizontal baseline. */
export function areaPath(pts: XY[], baselineY: number): string {
  if (pts.length < 2) return '';
  const last = pts[pts.length - 1]!;
  const first = pts[0]!;
  return `${monotonePath(pts)}L${fmt(last.x)},${fmt(baselineY)}L${fmt(first.x)},${fmt(baselineY)}Z`;
}

/**
 * Closed envelope between two smooth lines sharing x positions (the min/max
 * band behind an averaged trace). Both edges are monotone-smoothed.
 */
export function bandPath(top: XY[], bottom: XY[]): string {
  if (top.length < 2 || bottom.length < 2) return '';
  const back = [...bottom].reverse();
  const backFirst = back[0]!;
  return `${monotonePath(top)}L${fmt(backFirst.x)},${fmt(backFirst.y)}${monotoneSegments(back)}Z`;
}
